import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { getSupportUnread, markSupportSeen, getAdminUnread } from "@/lib/api";
import { creditsToTime } from "@/components/PricingSection";
import { WhatsNewDialog } from "@/components/WhatsNewDialog";
import { LayoutDashboard, Video, Receipt, LifeBuoy, Settings as SettingsIcon, LogOut, Coins, ShieldCheck } from "lucide-react";

const NAV_ITEMS = [
    { to: "/console", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/console/stream", icon: Video, label: "Start Stream", testid: "nav-stream" },
    { to: "/console/billing", icon: Receipt, label: "Billing", testid: "nav-billing" },
    { to: "/console/support", icon: LifeBuoy, label: "Support", testid: "nav-support" },
    { to: "/console/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings" },
];

const ADMIN_ITEM = { to: "/console/admin", icon: ShieldCheck, label: "Admin", testid: "nav-admin" };

const UNREAD_POLL_MS = 60000;

const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] border-l-2 transition-colors whitespace-nowrap ${
        isActive
            ? "border-blaze text-white bg-white/5"
            : "border-transparent text-white/50 hover:text-white hover:bg-white/5"
    }`;

function useUnreadBadges(isAdmin, pathname) {
    const [supportUnread, setSupportUnread] = useState(0);
    const [adminUnread, setAdminUnread] = useState(0);

    useEffect(() => {
        const load = () => getSupportUnread().then((d) => setSupportUnread(d.unread)).catch(() => {});
        load();
        const timer = setInterval(load, UNREAD_POLL_MS);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!isAdmin) return undefined;
        const load = () => getAdminUnread().then((d) => setAdminUnread(d.unread)).catch(() => {});
        load();
        const timer = setInterval(load, UNREAD_POLL_MS);
        const onSeen = () => setAdminUnread(0);
        window.addEventListener("admin-tickets-seen", onSeen);
        return () => {
            clearInterval(timer);
            window.removeEventListener("admin-tickets-seen", onSeen);
        };
    }, [isAdmin]);

    useEffect(() => {
        if (pathname === "/console/support" && supportUnread > 0) {
            markSupportSeen()
                .then(() => setSupportUnread(0))
                .catch(() => {});
        }
    }, [pathname, supportUnread]);

    return { supportUnread, adminUnread };
}

const NavBadge = ({ count, testId, className }) => {
    if (!count) return null;
    return (
        <span
            className={`${className} bg-blaze text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none`}
            data-testid={testId}
        >
            {count}
        </span>
    );
};

const NavItems = ({ items, supportUnread, adminUnread, mobile = false }) => (
    <>
        {items.map((item) => (
            <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
                data-testid={mobile ? `${item.testid}-mobile` : item.testid}
            >
                <item.icon className={mobile ? "w-3.5 h-3.5" : "w-4 h-4"} />
                {item.label}
                {item.testid === "nav-support" && (
                    <NavBadge
                        count={supportUnread}
                        testId={mobile ? "support-unread-badge-mobile" : "support-unread-badge"}
                        className={mobile ? "ml-1" : "ml-auto"}
                    />
                )}
                {item.testid === "nav-admin" && (
                    <NavBadge
                        count={adminUnread}
                        testId={mobile ? "admin-unread-badge-mobile" : "admin-unread-badge"}
                        className={mobile ? "ml-1" : "ml-auto"}
                    />
                )}
            </NavLink>
        ))}
    </>
);

const HeaderBar = ({ user, credits, onLogout }) => (
    <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="font-display font-bold uppercase tracking-tight text-lg shrink-0">
            SPCLIVE<span className="text-blaze">//</span>STUDIO
        </Link>
        <div className="flex items-center gap-3 sm:gap-5 min-w-0">
            <div
                className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-xs"
                data-testid="console-credits-badge"
            >
                <Coins className="w-3.5 h-3.5 text-blaze" />
                <span className="text-white font-bold">{credits.toLocaleString()}</span>
                <span className="text-white/40 hidden sm:inline">· ≈ {creditsToTime(credits)}</span>
            </div>
            <div className="font-mono text-[11px] text-white/50 hidden md:block truncate max-w-[160px]" data-testid="console-user-name">
                {user?.display_name}
            </div>
            <button
                onClick={onLogout}
                className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-blaze transition-colors"
                data-testid="logout-button"
            >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Logout</span>
            </button>
        </div>
    </div>
);

export default function ConsoleLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const credits = user?.credits ?? 0;
    const isAdmin = user?.role === "admin";
    const navItems = isAdmin ? [...NAV_ITEMS, ADMIN_ITEM] : NAV_ITEMS;
    const { supportUnread, adminUnread } = useUnreadBadges(isAdmin, location.pathname);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-obsidian text-white flex flex-col" data-testid="console-layout">
            <WhatsNewDialog />
            <header className="border-b border-white/10 bg-obsidian/90 backdrop-blur sticky top-0 z-40">
                <HeaderBar user={user} credits={credits} onLogout={handleLogout} />
                <nav className="flex md:hidden overflow-x-auto border-t border-white/10" data-testid="console-mobile-nav">
                    <NavItems items={navItems} supportUnread={supportUnread} adminUnread={adminUnread} mobile />
                </nav>
            </header>
            <div className="flex flex-1">
                <aside className="hidden md:flex flex-col w-56 border-r border-white/10 py-6 shrink-0" data-testid="console-sidebar">
                    <NavItems items={navItems} supportUnread={supportUnread} adminUnread={adminUnread} />
                </aside>
                <main className="flex-1 min-w-0 p-4 sm:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
